import async from 'async';
import _ from 'lodash';
import { i18n_t } from '@/locales/i18n';
import store from '@/renderer/store';
import TaskManager from '@/renderer/scripts/utils/TaskManager/BaseTaskManager';
import { TRANSFER_LIMIT, TransferSetting } from '@/renderer/scripts/utils/ConstantConfig';
import {
  ProgressParams,
  SubTask,
  Task,
  TaskEvents,
  TaskManagerOptions,
  TaskStatus,
} from '@/renderer/scripts/utils/TaskManager/types';
import { IS_BROWSER } from '@/common/constant/index';
import smh from '@/renderer/scripts/smh';
import tips from '@/renderer/scripts/utils/tips';
import storage from '@/renderer/scripts/utils/storage';
import { FileInfo } from '@/renderer/scripts/utils/upload/types';
import { transformErrorForSerialization } from '@/common/tools';

import { TaskStatusUtil } from '@/renderer/scripts/utils/TaskManager/utils/TaskUtils';
import { BaseTaskManagerHook } from '@/renderer/scripts/utils/TaskManager/types/hooks';
import { CommonLoader } from '@/renderer/scripts/utils/CommonLoader';
import Uploader from './Uploader';

// const { retryLimit, chunkParallelLimit, batchLimit: storeBatchLimit } =
//   storage.get<TransferSetting>(storage.StoreKey.UPLOAD_TRANSFER_SETTING);
const settingKey = storage.StoreKey.UPLOAD_TRANSFER_SETTING; // 上传相关系统配置-key
export const uploadOptions: TaskManagerOptions<FileInfo> = {
  type: 'upload',
  batchLimit: storage.get<TransferSetting>(settingKey)?.batchLimit || TRANSFER_LIMIT.upload.batchLimit,
  onStart(params: BaseTaskManagerHook<FileInfo>) {
    const { task, onProgress, onFinish, onSubTaskFinish, onError, onReady, onUnReady, onPreparing, onChecking } =
      params;
    task.successNum = task.successNum || 0;
    task.errorNum = task.errorNum || 0;
    task.totalNum = task.getListLength();

    // 子任务容量池大小
    const subTaskPool: number = this.batchLimit - (this as unknown as TaskManager<FileInfo>).getRunningSubTaskMapSize();
    // 若子任务容量池还很大，则只开启对应的文件长度的并发（减少消耗）
    // 若子任务容量池还很大，则可以用所有的容量池进行遍历
    const subTaskLimit = Math.min(subTaskPool, task.getListLength());
    // 正在运行的数量
    async.forEachLimit(
      task.getList(),
      subTaskLimit,
      (subTask: SubTask<FileInfo>, next) => {
        const removeEvents = () => {
          subTask.ins &&
            subTask.ins
              .off('hashProgress', hashProgressFn)
              .off('progress', progressFn)
              .off('status_changed', statusChangedFn)
              .off('success', successFn)
              .off('error', errorFn)
              .off('check_hash', checkHashFn);
        };
        const bindEvnets = () => {
          subTask.ins &&
            subTask.ins
              .on('hashProgress', hashProgressFn)
              .on('ioPercent', ioPercentFn)
              .on('progress', progressFn)
              .on('status_changed', statusChangedFn)
              .once('success', successFn)
              .once('error', errorFn)
              .on('check_hash', checkHashFn);
        };
        const hashProgressFn = (progressInfo: Uploader.ProgressInfo) => {
          subTask.hashPercent = progressInfo.percent;
          subTask.speed = progressInfo.speed;
          onProgress();
        };
        const ioPercentFn = ({ loaded, total }: { loaded: number; total: number }) => {
          let ioPercent = _.divide(loaded, total);
          ioPercent = _.floor(ioPercent, 3); // 向下舍入
          subTask.ioPercent = ioPercent;
          onProgress({ ioPercent });
        };

        const progressFn = (progressInfo: Uploader.ProgressInfo) => {
          const { percent, speed, remainingTime, remainingMessage, loaded } = progressInfo;
          subTask.percent = percent;
          subTask.speed = speed;
          subTask.loaded = loaded;
          subTask.remainingTime = remainingTime;
          subTask.remainingMessage = remainingMessage;
          const totalSpeed = (this as unknown as TaskManager<FileInfo>).getTotalSpeed(task.taskId);
          onProgress({
            speed: totalSpeed,
          });
        };

        const statusChangedFn = (value: TaskStatus) => {
          subTask.status = value;
          onProgress({ subTaskStatus: value });
          if (subTask.status === TaskStatus.running) {
            onReady();
          }
          if (subTask.status === TaskStatus.checking) {
            onChecking();
          }
          if (subTask.status === TaskStatus.preparing) {
            onPreparing();
          }
          if (subTask.status === TaskStatus.unReady) {
            onUnReady(new Error(i18n_t('本地文件不正确')));
          }
          if (value === TaskStatus.canceled || value === TaskStatus.paused) {
            (this as unknown as TaskManager<FileInfo>).deleteSubTaskInMap(subTask);
            removeEvents();
            next();
          }
        };
        const successFn = (successInfo: CoFile.UploadFinishedParams) => {
          subTask.status = TaskStatus.success;
          if (successInfo.uploadType === 'quickUpload') {
            subTask.isQuickUpload = true;
            if (task.getListLength() === 1) {
              task.isQuickUpload = true;
            }
          }
          if (!_.isUndefined(task.successNum) && !_.isUndefined(task.errorNum)) {
            task.successNum++;
            onProgress({
              progressText: `${task.successNum + task.errorNum}/${task.totalNum}`,
            });
          }
          const quickObject = subTask.ins?.getQuickUploadInfo();
          const quickUploadResult = quickObject?.quickUploadResult || 0;
          const quickUploadError = quickObject?.quickUploadError || '';
          (this as unknown as TaskManager<FileInfo>).deleteSubTaskInMap(subTask);
          // 上传成功上报
          reporter.report('upload_finished', {
            upload_type: successInfo.uploadType,
            upload_result: 'successful',
            whether_extremely: subTask.isQuickUpload ? 1 : 0,
            upload_extremely_result: quickUploadResult,
            upload_extremely_fail: quickUploadError,
            file_name: subTask?.info?.name || '',
          });
          onSubTaskFinish(subTask);
          removeEvents();
          next();
        };
        const errorFn = (err: any) => {
          subTask.status = TaskStatus.error;
          if (!_.isUndefined(task.successNum) && !_.isUndefined(task.errorNum)) {
            task.errorNum++;
            const progressTriggerParam: ProgressParams = {
              progressText: `${task.successNum + task.errorNum}/${task.totalNum}`,
            };
            onProgress(progressTriggerParam);
          }
          logger.ui.error('[Upload]', JSON.stringify(transformErrorForSerialization(err)));
          subTask.error = err;
          const quickObject = subTask.ins?.getQuickUploadInfo();
          const quickUploadResult = quickObject?.quickUploadResult || 0;
          const quickUploadError = quickObject?.quickUploadError || '';
          (this as unknown as TaskManager<FileInfo>).deleteSubTaskInMap(subTask);
          // 上传失败上报
          reporter.report('upload_finished', {
            upload_type: err.uploadType,
            upload_result: 'failed',
            status: err.status || 0,
            code: err.code || '',
            message: err.message || '',
            upload_extremely_result: quickUploadResult,
            upload_extremely_fail: quickUploadError,
            file_name: subTask?.info?.name || '',
          });
          removeEvents();
          next();
        };
        const checkHashFn = (data: Uploader.CheckHashInfo) => {
          // 状态检查
          if (task.status !== TaskStatus.running && task.status !== TaskStatus.checking) {
            return;
          }
          if (data.start) {
            subTask.status = TaskStatus.checking;
            subTask.hashPercent = data.percent;
            if (task.getListLength() === 1) {
              task.status = TaskStatus.checking;
              task.hashPercent = data.percent;
            } else {
              // TODO loganylwu 这里的有速度，其实是因为task进入了running状态带来的问题，后续优化了状态之后，就可以再看一下这里。
              // 解决上传文件夹时秒传有上传速度的问题
              const existRunningSubTask = task.getList().filter(subTask => subTask.status === TaskStatus.running);
              if (!existRunningSubTask.length) {
                task.speed = 0;
              }
            }
          }
          // hash检查结束进入->Task.preparing
          else {
            subTask.status = TaskStatus.preparing;
            subTask.hashPercent = data.percent;
            if (task.getListLength() === 1) {
              task.status = TaskStatus.preparing;
              task.hashPercent = data.percent;
            }
          }
          onProgress();
        };

        const taskIsStop = TaskStatusUtil.isStopped(task.status);
        const subTaskIsFinish = subTask.ins && !subTask.ins.isRunning() && subTask.status !== TaskStatus.paused;
        const subTaskIsSuccess = subTask.status === TaskStatus.success;
        // 可以跳过当前子任务
        const canSkip = taskIsStop || subTaskIsFinish || subTaskIsSuccess;
        if (canSkip) {
          removeEvents();
          next();
          return;
        }
        if (!subTask.ins) {
          subTask.ins = new Uploader(subTask.info, {
            smh,
          });
        }

        bindEvnets();
        // 在这里第一次才可以拿到ins,要尽快在ins启动前，修改其状态waiting->ready
        subTask.ins.setStatus(TaskStatus.ready);
        subTask.ins.start();
        (this as unknown as TaskManager<FileInfo>).insertSubTaskInMap(subTask);
      },
      () => {
        setTimeout(() => {
          const { successNum = 0, errorNum = 0 } = task;
          const finishNum = successNum + errorNum;
          (this as unknown as TaskManager<FileInfo>).clearSubTaskInMap(task.taskId);

          // 若当前任务不是正在运行的任务 || 任务未完成
          if (!TaskStatusUtil.isRunning(task.status) || task.totalNum !== finishNum) {
            return;
          }
          task.finishDate = Date.now();
          const hasErr = (task.errorNum as number) > 0;
          if (hasErr) {
            onError(hasErr);
          } else {
            onFinish();
          }
        }, 200);
      },
    );
  },
  onPause(task: Task<FileInfo>) {
    // 停止任务的操作，例如停止该文件/文件夹的上传
    async.forEachLimit(task.getList(), 16, (subTask, next) => {
      // 根据ins和status分别判断任务状态
      const insNotFinish = subTask.ins && !subTask.ins.isFinished();
      const statusNotFinish = ![TaskStatus.success, TaskStatus.error, TaskStatus.canceled].includes(subTask.status);
      const subTaskIsNotFinish = insNotFinish && statusNotFinish;
      if (subTaskIsNotFinish) {
        subTask.status = TaskStatus.paused;
        subTask.ins.pause();
      }
      next();
    });
  },
  onRetry(params: { task: Task<FileInfo>; onFinish: Function; onProgress: Function }) {
    const { task, onProgress, onFinish } = params;
    // 重试错误任务的操作
    async.forEachLimit(
      task.getList(),
      16,
      (subTask, next) => {
        if (subTask.status === TaskStatus.error) {
          delete subTask.ins;
        }
        next();
      },
      () => {
        task.errorNum = 0; // 重置错误进度
        const finishNum = task.successNum || 0 + task.errorNum;
        task &&
          onProgress({
            progressText: `${finishNum}/${task.totalNum}`,
          });
        onFinish();
      },
    );
  },
  onCancel(task: Task<FileInfo>) {
    const { isLogined } = store.getters;

    // 这里进行取消任务的操作，例如取消该文件的上传
    async.forEachLimit(task.getList(), 16, (subTask, next) => {
      const loader = subTask.ins as CommonLoader<FileInfo>;
      if (loader && !loader.isFinished()) {
        loader.cancel(false, isLogined);
        subTask.ins = null;
      }
      next();
    });
  },
  onFinish(params: { list: Task<FileInfo>[] }) {
    // 所有任务都执行完毕
    const { list } = params;
  },
};
const uploadTaskManager = new TaskManager<FileInfo>(uploadOptions);
const showSingleQuickUploadSuccessTips = _.debounce(
  () => {
    tips.success(i18n_t('秒传成功'));
  },
  5000,
  {
    leading: true,
  },
);

// 单个任务完成的通知
uploadTaskManager.on(TaskEvents.taskFinish, (task: Task<FileInfo>) => {
  if (IS_BROWSER) {
    return;
  }
  if (task.isQuickUpload) {
    showSingleQuickUploadSuccessTips();
  } else if (task.getListLength() > 1) {
    // 判断多个上传是否有秒传成功
    let quickUploadCount = 0;
    task.getList().forEach(v => {
      if (v.isQuickUpload) {
        quickUploadCount++;
      }
    });
    if (quickUploadCount > 0) {
      tips.success(
        i18n_t('成功上传{slot1}个文件，其中有{quickUploadCount}个秒传', {
          slot1: task.getListLength(),
          quickUploadCount,
        }),
      );
    }
  }
});
export default uploadTaskManager;

/**
 * @description 将文件（夹）列表处理成任务
 * @export
 * @param {Record<string, FileInfo[]>} fileMap 文件对
 * @param {Record<string, string>} folderNameChangeMap 需要改变文件夹名
 * @return {*}  {Promise<Task<FileInfo>[]>}
 */
export async function handleUploadFilesToTask(fileMap: Record<string, FileInfo[]>): Promise<Task<FileInfo>[]> {
  const formatSubTask = (files: FileInfo[], taskId: string) =>
    async.mapLimit<FileInfo, SubTask<FileInfo>>(files, 16, (file, next) => {
      // 声明一个子任务对象
      const subTask: SubTask<FileInfo> = {
        id: TaskManager.getTaskId('subTask_'),
        taskId,
        title: file.name,
        status: TaskStatus.waiting,
        percent: 0,
        hashPercent: 0,
        ioPercent: 0,
        speed: 0,
        info: file,
        remainingTime: 0,
        remainingMessage: '',
      };
      if (file.name.length > 200) {
        subTask.status = TaskStatus.error;
        subTask.error = { code: 'FileNameLengthExceed' };
      }
      next(null, subTask);
    });
  const isEmptyFolder = (fileInfo: FileInfo[], mapKey: string) => {
    return fileInfo[0] !== undefined && fileInfo.length === 1 && mapKey === fileInfo[0]?.name;
  };
  const isSelfDir = (fileInfo: FileInfo, mapKey: string) => {
    return !(fileInfo.isDir && fileInfo.name === mapKey);
  };
  return async.mapLimit<string, Task<FileInfo>>(Object.keys(fileMap), 16, (mapKey, next) => {
    const taskId = TaskManager.getTaskId();
    const title = mapKey;
    const emptyFolder = isEmptyFolder(fileMap[mapKey], mapKey);
    let filesOrFolders: FileInfo[] = [];
    filesOrFolders = fileMap[mapKey].filter(item => {
      return emptyFolder ? true : isSelfDir(item, mapKey);
    }); // 过滤掉多余的文件夹信息（在之前步骤已经创建）
    formatSubTask(filesOrFolders, taskId).then(subTasks => {
      // 过滤掉根文件夹本身
      const list = subTasks.filter(subTask => {
        const { isDir, remoteRootFolder, remotePath } = subTask.info;
        return !isDir || (isDir && remoteRootFolder !== remotePath);
      });
      const task: Task<FileInfo> = {
        id: TaskManager.getTaskId('task_'),
        title,
        taskId,
        action: 'upload',
        getList: () => list,
        getListLength: () => list.length,
        getItem: index => list[index],
        sortListByStatus: () => {
          list.sort((a, b) => {
            if (a.status === 'error' && b.status === 'success') return -1;
            if (a.status === 'success' && b.status === 'error') return 1;
            return 0;
          });
        },
        size: filesOrFolders[0]?.folderSize,
        status: TaskStatus.waiting,
        progressText: `0/${subTasks.length}`,
        speed: 0,
        finishDate: 0,
        error: undefined,
        isDir: true,
        hashPercent: 0,
        ioPercent: 0,
      };
      if (list.length === 1) {
        // 若该上传任务只有一个文件则需要额外判断
        task.isDir = list[0].info.name !== title;
      }
      next(null, task);
    });
  });
}
